import {action, observable} from 'mobx';

export class FormFieldValidationResult
{
    public constructor(public readonly isValid:boolean,
                       public readonly errorMessage:string,
                       public readonly stopPropagation:boolean = false)
    {
    }

    public static validResult(validMessage:string = ''):FormFieldValidationResult
    {
        return new FormFieldValidationResult(true, validMessage);
    }

    public static invalidResult(errorMessage:string = ''):FormFieldValidationResult
    {
        return new FormFieldValidationResult(false, errorMessage);
    }
}

export interface IFormField<T>
{
    validate:(isSubmit?:boolean) => Promise<FormFieldValidationResult>;
    onSubmit:() => void;
    getValidationResult:() => FormFieldValidationResult;
    getInitialValue:() => T;
    getValue:() => T;
    focus:() => void;
    blur:() => void;
    reset:(value?:T) => void;
    change:(newValue?:T) => Promise<FormFieldValidationResult>;
    userInput:(newValue?:T) => void;
}

export interface IFormData
{
    [fieldKey:string]:string | boolean | number | null;
}

export type FormSubmitHandler = (formData:IFormData) => void;
export type FormChangeHandler = (fieldKey:string, fieldValue:any, validationResult:FormFieldValidationResult) => void;
export type FormValidateHandler = (fieldKey:string, fieldValue:any, validationResult:FormFieldValidationResult) => void;

export interface IFormProps
{
    onSubmit?:FormSubmitHandler;
    onChange?:FormChangeHandler;
    onValidated?:FormValidateHandler;
}

interface IFieldsMap
{
    [fieldKey:string]:IFormField<any>;
}

interface IServerErrors
{
    [fieldKey:string]:string;
}

export class Form
{
    private readonly _fields:IFieldsMap = {};
    private readonly _props:IFormProps;

    @observable
    private _serverErrors:IServerErrors | null = null;

    public constructor(props?:IFormProps)
    {
        this._props = props;
    }

    // -------------------------------------------------------------------------

    public registerField(fieldKey:string, fieldRef:IFormField<any>):void
    {
        if( this._fields[fieldKey] )
            console.warn(`Form: fieldKey ${fieldKey} already defined`);

        this._fields[fieldKey] = fieldRef;
    }

    public unregisterField(fieldKey:string):void
    {
        delete this._fields[fieldKey];
    }

    // -------------------------------------------------------------------------

    public async validate(isSubmit:boolean = false):Promise<FormFieldValidationResult>
    {
        const validationPromises:Array<Promise<FormFieldValidationResult>> = [];

        this.getFields().forEach((field:IFormField<any>) =>
        {
            if( isSubmit )
                field.onSubmit();

            validationPromises.push(field.validate(isSubmit));
        });

        await Promise.all(validationPromises);

        return new FormFieldValidationResult(this.areAllFieldsValid(), 'Form is invalid');
    }

    public async submit():Promise<boolean>
    {
        const validationResult:FormFieldValidationResult = await this.validate(true);

        if( validationResult.isValid && this._props && this._props.onSubmit )
        {
            const formData:IFormData = this.getFormData();

            this._props.onSubmit(formData);

            return true;
        }

        return false;
    }

    public onFieldChanged<T>(fieldKey:string):void
    {
        const field:IFormField<T> = this._fields[fieldKey];

        if( !field )
        {
            console.warn(`Form: fieldKey ${fieldKey} not found`);
            return;
        }

        if( this._props && this._props.onChange )
            this._props.onChange(fieldKey, field.getValue(), field.getValidationResult());
    }

    public onValidated(fieldKey:string, value:any, validationResult:FormFieldValidationResult):void
    {
        if( this._props && this._props.onValidated )
            this._props.onValidated(fieldKey, value, validationResult);
    }

    // -------------------------------------------------------------------------

    public get<T>(fieldKey:string):T
    {
        const field:IFormField<T> = this._fields[fieldKey];

        if( !field )
        {
            console.warn(`Form: fieldKey ${fieldKey} not found`);
            return null;
        }

        return field.getValue();
    }

    public getField<T>(fieldKey:string):IFormField<T>
    {
        const field:IFormField<T> = this._fields[fieldKey];

        if( !field )
        {
            console.warn(`Form: fieldKey ${fieldKey} not found`);
            return null;
        }

        return field;
    }

    public getFields(keys?:Array<string>):Array<IFormField<any>>
    {
        if( !keys )
            return Object.values(this._fields);

        return keys.map((fieldKey:string) => this._fields[fieldKey]);
    }

    public getFormData():IFormData
    {
        const formData:IFormData = {};

        Object.keys(this._fields).forEach(
            (fieldKey:string) => formData[fieldKey] = this._fields[fieldKey].getValue()
        );

        return formData;
    }

    // -------------------------------------------------------------------------

    public areAllFieldsValid(keys?:string[]):boolean
    {
        if( !keys )
            keys = Object.keys(this._fields);

        return keys.every((fieldKey:string) =>
            {
                const field:IFormField<any> = this._fields[fieldKey];

                if( !field )
                    return false;

                const validationResult:FormFieldValidationResult = field.getValidationResult();

                return validationResult && validationResult.isValid;
            }
        );
    }

    public areSomeFieldChanged(keys?:string[]):boolean
    {
        if( !keys )
            keys = Object.keys(this._fields);

        return keys.some((fieldKey:string) =>
            {
                const field:IFormField<any> = this._fields[fieldKey];

                if( !field )
                    return false;

                const initialValue:any = field.getInitialValue();
                const value:any = field.getValue();

                return initialValue !== value;
            }
        );
    }

    public areSomeFieldIsEmpty(keys?:string[]):boolean
    {
        if( !keys )
            keys = Object.keys(this._fields);

        return keys.some((fieldKey:string) =>
            {
                const field:IFormField<any> = this._fields[fieldKey];

                if( !field )
                    return false;

                const value:any = field.getValue();

                return !value;
            }
        );
    }

    // -------------------------------------------------------------------------

    @action
    public setServerErrors(errorMessageFromServer:string)
    {
        let _serverErrors:IServerErrors = {};

        let errorLines:string[] = errorMessageFromServer.split('\n');

        errorLines.forEach(errorLine =>
        {
            if( !errorLine ) return;

            const error = this._parseError(errorLine);

            if( !_serverErrors[error.fieldName] )
                _serverErrors[error.fieldName] = error.message;
            else
                _serverErrors[error.fieldName] += '|' + error.message;
        });

        this._serverErrors = _serverErrors;

        Object.keys(this._serverErrors).forEach(fieldName =>
        {
            if( fieldName === '_' ) return;
            const field = this.getField(fieldName);
            if( field ) field.validate();
        });
    }

    @action
    public setServerError(fieldName:string, errorMessage:string)
    {
        let _serverErrors:IServerErrors = {};

        _serverErrors[fieldName] = errorMessage;

        this._serverErrors = _serverErrors;

        if( fieldName === '_' ) return;

        const field = this.getField(fieldName);
        if( field ) field.validate();
    }

    private _parseError(errorMessage:string)
    {
        const re = /^\s\s\* (.*): (.*)$/;

        let fieldName = '_';
        let message = errorMessage;

        const hasFiled = errorMessage.match(re);

        if( hasFiled )
        {
            fieldName = hasFiled[1];
            message = hasFiled[2];
        }

        return {fieldName, message};
    }

    public getServerError(fieldKey:string)
    {
        const serverErrors:IServerErrors = this._serverErrors;

        if( !serverErrors || !serverErrors[fieldKey] ) return null;

        return serverErrors[fieldKey];
    }

    @action
    public removeServerError(fieldKey:string)
    {
        const serverErrors:IServerErrors = this._serverErrors;

        if( !serverErrors ) return;

        let _serverErrors:IServerErrors;

        Object.keys(serverErrors).forEach(key =>
        {
            if( key !== '_' && key != fieldKey )
            {
                if( !_serverErrors ) _serverErrors = {};
                _serverErrors[key] = serverErrors[key];
            }
        });

        this._serverErrors = _serverErrors;
    }

    @action
    public removeServerErrors()
    {
        this._serverErrors = null;
    }
}
